import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RuleInterface } from '@vendasta/automata/lib/_internal/interfaces/choice.interface';
import { And, Comparison, Rule } from '@vendasta/automata';
import { ActionComparison } from './crm-created-modified-selector.component';

export const createdVariablePath = '{.created}';
export const updatedVariablePath = '{.updated}';
export const changeTypeVariablePath = '{.change_type}';
export const changeTypeVariable = 'change_type';
export const fieldsChangedVariablePath = '{.fields_changed}';
export const fieldsChangedVariable = 'fields_changed';

export const CREATED = 'CREATED';
export const UPDATED = 'UPDATED';

const comparisonToActionKey = new Map<ActionComparison, string>([
  [ActionComparison.Modified, 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_TASK_CREATED_CONTACT.MODIFIED'],
  [ActionComparison.Created, 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_TASK_CREATED_CONTACT.CREATED'],
  [ActionComparison.Any, 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_TASK_CREATED_CONTACT.CREATED_OR_MODIFIED'],
]);

@Injectable({
  providedIn: 'root',
})
export class CrmCreatedModifiedSelectorService {
  constructor(protected readonly translateService: TranslateService) {}

  public findActionRuleComparison(rules: RuleInterface): Comparison {
    if (!rules) {
      return Comparison.AlwaysTrue;
    }

    const andRule = Rule.fromRuleInterface(rules) as And;
    return (
      andRule.value?.find(
        (rule) => rule.getVariablePath() === createdVariablePath && rule.value === updatedVariablePath,
      )?.comparison || Comparison.AlwaysTrue
    );
  }

  public findActionComparison(rule: RuleInterface | undefined): ActionComparison {
    if (!rule) {
      return ActionComparison.Any;
    }

    const andRule = Rule.fromRuleInterface(rule) as And;
    const changeTypeValue = andRule.value?.find((rule) => rule.getVariablePath()?.includes(changeTypeVariable))?.value;
    const hasFieldsChangedPath = andRule.value?.find((rule) => rule.getVariablePath()?.includes(fieldsChangedVariable));

    const oldComparison = this.findActionRuleComparison(rule);

    // Create a switch which looks at changeTypeValue and determines which ActionComparison to return
    switch (changeTypeValue) {
      case CREATED:
        if (hasFieldsChangedPath) {
          return ActionComparison.Any;
        }
        return ActionComparison.Created;
      case UPDATED:
        return ActionComparison.Modified;
      case undefined:
        // This allows backwards compatability
        switch (oldComparison) {
          case Comparison.TimeEquals:
            return ActionComparison.Created;
          case Comparison.TimeBefore:
            return ActionComparison.Modified;
          default:
            return ActionComparison.Any;
        }
      default:
        return ActionComparison.Any;
    }
  }

  findFieldChangedValue(rule: RuleInterface | undefined): string[] | undefined {
    if (!rule) {
      return undefined;
    }

    const andRule = Rule.fromRuleInterface(rule) as And;
    return andRule.value.find((rule) => {
      return rule.getVariablePath()?.includes(fieldsChangedVariable);
    })?.value as string[];
  }

  public findActionString(rule: RuleInterface | undefined): string {
    if (!rule) {
      return '';
    }
    const actionComparison = this.findActionComparison(rule);
    return this.translateService.instant(comparisonToActionKey.get(actionComparison) || '').toLowerCase();
  }
}
